/**
 * User-facing error copy. The backend owns the text of its errors (`detail`, in Spanish)
 * and sends a stable `error.code` for logic. This catalog only covers errors raised in
 * the browser and generic failures whose server text is not meant for users.
 */

const SERVER_UNAVAILABLE = 'No pudimos conectar con el servidor. Intenta de nuevo en unos segundos.';

export const ERROR_MESSAGES: Record<string, string> = {
    // Raised by the ePayco card tokenizer in the browser (CardFields)
    provider_unavailable: 'No pudimos comunicarnos con la pasarela de pagos. Intenta de nuevo en unos minutos.',
    card_tokenize_failed: 'Revisa el número, la fecha y el código de seguridad de tu tarjeta.',
    insufficient_funds: 'La tarjeta no tiene fondos suficientes. Intenta con otra tarjeta.',
    risk_declined: 'El pago no fue aprobado. Intenta con otra tarjeta.',
    card_declined: 'Tu banco no aprobó el pago. Intenta con otra tarjeta.',

    // Generic
    VALIDATION_ERROR: 'Revisa los datos ingresados.',
    NETWORK_ERROR: SERVER_UNAVAILABLE,
    HTTP_404: SERVER_UNAVAILABLE,
    INTERNAL_ERROR: SERVER_UNAVAILABLE,
};

/** Error raised by frontend code with a catalog code. */
export class UserFacingError extends Error {
    constructor(public readonly code: string) {
        super(ERROR_MESSAGES[code] ?? code);
        this.name = 'UserFacingError';
    }
}

/** Codes whose text is decided by the frontend even if the backend sent a `detail`. */
function isGenericCode(code: string, status?: number): boolean {
    return code === 'VALIDATION_ERROR'
        || code === 'INTERNAL_ERROR'
        || code === 'HTTP_404'
        || (code.startsWith('HTTP_') && (status ?? 0) >= 500);
}

/** Phrase shown when a charge that was in review finally closes. */
export function paymentCloseMessage(code?: string | null): string {
    if (code === 'insufficient_funds') return ERROR_MESSAGES.insufficient_funds;
    if (code === 'risk_declined') return ERROR_MESSAGES.risk_declined;
    return ERROR_MESSAGES.card_declined;
}

export function errorCode(err: any): string | undefined {
    if (err instanceof UserFacingError) return err.code;
    if (err?.isAxiosError && !err.response) return 'NETWORK_ERROR';
    const code = err?.response?.data?.error?.code;
    return typeof code === 'string' ? code : undefined;
}

export function userErrorMessage(err: any, fallback: string): string {
    if (err instanceof UserFacingError) return err.message;

    const code = errorCode(err);
    if (code === 'NETWORK_ERROR') return ERROR_MESSAGES.NETWORK_ERROR;

    const response = err?.response;
    if (response) {
        const status: number | undefined = response.status;
        if (code && isGenericCode(code, status)) return ERROR_MESSAGES[code] ?? SERVER_UNAVAILABLE;
        if (!code && (status ?? 0) >= 500) return SERVER_UNAVAILABLE;

        const detail = response.data?.detail;
        if (typeof detail === 'string' && detail.trim()) return detail;
        return fallback;
    }

    return fallback;
}
